// src/iTwin/export/ifc/core/guid.ts
const IFC64 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$";

function uuidV4(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as any).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Normalize to a valid IfcGloballyUniqueId (22 chars).
 * - If input already 22 chars, returns it (optionally validate charset).
 * - If input is UUID (with or without dashes/braces), compress it.
 * - Otherwise, generate a new GUID.
 */
export function toIfcGuid(input?: string): string {
  if (input && input.length === 22) return input;

  const cleaned = (input ?? uuidV4()).replace(/[{}-]/g, "").toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(cleaned)) {
    return compressUuidToIfc(uuidV4());
  }
  return compressHex32ToIfc(cleaned);
}

function compressUuidToIfc(uuid: string): string {
  const hex32 = uuid.replace(/[{}-]/g, "").toLowerCase();
  return compressHex32ToIfc(hex32);
}

function compressHex32ToIfc(hex32: string): string {
  const bytes = hexToBytes(hex32); // 16 bytes
  return encodeIfc64(bytes);       // 22 chars
}

function hexToBytes(hex32: string): Uint8Array {
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = parseInt(hex32.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Base64-like encoding with IFC64 alphabet.
 * 16 bytes -> 22 chars (same as base64 without '==').
 */
function encodeIfc64(bytes: Uint8Array): string {
  let out = "";
  let i = 0;

  // full triplets -> 4 chars
  for (; i + 2 < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];

    out += IFC64[b0 >> 2];
    out += IFC64[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += IFC64[((b1 & 0x0f) << 2) | (b2 >> 6)];
    out += IFC64[b2 & 0x3f];
  }

  // remainder (for 16 bytes, remainder is exactly 1 byte -> 2 chars)
  const rem = bytes.length - i;
  if (rem === 1) {
    const b0 = bytes[i];
    out += IFC64[b0 >> 2];
    out += IFC64[(b0 & 0x03) << 4];
  } else if (rem === 2) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    out += IFC64[b0 >> 2];
    out += IFC64[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += IFC64[(b1 & 0x0f) << 2];
  }

  return out;
}
